import { useState } from "react";
import { Checkbox } from "swing-components";
import { translate } from "utils/string-table";

import {
  ModalSubFeedbackBody,
  ModalSubFeedbackFooter,
  ModalSubFeedbackHeader,
  PositiveData,
  PositiveFeedbackReasons,
  PositiveStep,
} from "~components/ModalSubFeedback";

/********* Types ********/
type UpdatedPositiveReasons = Omit<PositiveData, "comment">;

type ModalSubFeedbackContentPositiveReasonsProps = {
  goBackToRatingStep: () => void;
  onDismiss: () => void;
  positiveData: PositiveData;
  setPositiveData: (data: PositiveData) => void;
  setPositiveStep: (step: PositiveStep) => void;
};

/********* Component ********/
export function ModalSubFeedbackContentPositiveReasons(
  props: ModalSubFeedbackContentPositiveReasonsProps,
) {
  const { goBackToRatingStep, onDismiss, positiveData, setPositiveData, setPositiveStep } = props;

  /********* Hooks ********/
  const [_updatedReasons, _setUpdatedReasons] = useState<UpdatedPositiveReasons>({
    reasons: positiveData?.reasons,
    otherReason: positiveData?.otherReason ?? "",
  });

  /********* Handlers ********/
  function handleCheckboxChange(reason: PositiveFeedbackReasons, isChecked: boolean) {
    _setUpdatedReasons((prev) => {
      const newReasons = isChecked
        ? [...prev.reasons, reason]
        : prev.reasons.filter((r) => r !== reason);

      // If "Other" is unchecked, remove otherReasons
      const newOtherReason = reason === "REASON_OTHER" && !isChecked ? undefined : prev.otherReason;

      return { ...prev, reasons: newReasons, otherReason: newOtherReason };
    });
  }

  function handleOtherReason(reason: string) {
    _setUpdatedReasons((prev) => ({ ...prev, otherReason: reason }));
  }

  function updatePositiveDataAndProceed(nextStep?: PositiveStep) {
    const updatedData = {
      ...positiveData,
      ..._updatedReasons,
      // Don't add otherReason field if it's empty
      ...(_updatedReasons.otherReason?.trim() && { otherReason: _updatedReasons.otherReason }),
    };
    // Persist updated feedback data before navigating
    setPositiveData(updatedData);

    if (!nextStep) {
      goBackToRatingStep();
      return;
    }

    setPositiveStep(nextStep);
  }

  /********* Constants ********/
  const selectedReasons = Object.keys(PositiveReasonsSelections) as PositiveFeedbackReasons[];

  const isNextDisabled =
    !_updatedReasons.reasons?.length ||
    (_updatedReasons.reasons?.includes("REASON_OTHER") && !_updatedReasons.otherReason?.trim());

  /********* Render ********/
  return (
    <>
      <ModalSubFeedbackHeader title={translate("GIVE_FEEDBACK_LABEL")} onDismiss={onDismiss} />
      <ModalSubFeedbackBody>
        <p css={{ fontWeight: 600 }}>{translate("MSF_POSITIVE_FEEDBACK_REASON_LABEL")}</p>
        <p>{translate("LABEL_SELECT_ALL_THAT_APPLY")}</p>
        <div css={{ marginTop: 16 }}>
          {selectedReasons.map((reason) => {
            const isChecked = _updatedReasons.reasons.includes(reason);
            return (
              <Checkbox
                key={reason}
                label={PositiveReasonsSelections[reason]}
                isChecked={isChecked}
                onCheckedChange={(checked: boolean) => handleCheckboxChange(reason, checked)}
                hasBorderBottom={reason !== "REASON_OTHER"}
                hasInputField={reason === "REASON_OTHER"}
                textAreaProps={{
                  isRequired: true,
                  label: translate("MSF_REASON_OTHER_TEXT_INPUT_LABEL"),
                  value: _updatedReasons.otherReason,
                  onChange: (reason: string) => handleOtherReason(reason),
                  placeholder: translate("MSF_REASON_OTHER_TEXT_INPUT_PLACEHOLDER"),
                }}
              />
            );
          })}
        </div>
      </ModalSubFeedbackBody>
      <ModalSubFeedbackFooter
        buttonPrimary={{
          onClick: () => updatePositiveDataAndProceed("comments"),
          isDisabled: isNextDisabled,
          text: translate("LABEL_NEXT"),
        }}
        buttonSecondary={{
          onClick: () => updatePositiveDataAndProceed(),
          isDisabled: false,
          text: translate("LABEL_BACK"),
        }}
      />
    </>
  );
}

// Note: Order matters here - it controls the order the UI displays the reasons
export const PositiveReasonsSelections: Record<PositiveFeedbackReasons, string> = {
  REASON_DISENGAGED: translate("MSF_POSITIVE_FEEDBACK_REASON_DISENGAGED"),
  REASON_FLEXIBLE: translate("MSF_POSITIVE_FEEDBACK_REASON_FLEXIBLE"),
  REASON_MANAGEMENT: translate("MSF_POSITIVE_FEEDBACK_REASON_MANAGEMENT"),
  REASON_KNOWLEDGEABLE: translate("MSF_POSITIVE_FEEDBACK_REASON_KNOWLEDGEABLE"),
  REASON_ATTITUDE: translate("MSF_POSITIVE_FEEDBACK_REASON_ATTITUDE"),
  REASON_RELIABILITY: translate("MSF_POSITIVE_FEEDBACK_REASON_RELIABILITY"),
  REASON_OTHER: translate("MSF_REASON_OTHER"),
};
